import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import type { UpdateGroupContentInput } from '@news-cms/shared';
import { buttonVariants } from '@/components/ui/button';
import { GroupContentForm } from '@/components/group-contents/group-content-form';
import { SuperAdminFallback } from '@/components/auth/super-admin-fallback';
import { useSession } from '@/lib/auth';
import { groupContentDetailQueryOptions, useUpdateGroupContent } from '@/lib/group-contents';

export const Route = createFileRoute('/_authenticated/group-contents/$id/edit')({
  component: EditGroupContentPage,
});

function EditGroupContentPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const query = useQuery(groupContentDetailQueryOptions(id));
  const update = useUpdateGroupContent(id);

  const handleSubmit = async (input: UpdateGroupContentInput) => {
    try {
      await update.mutateAsync(input);
      await navigate({ to: '/group-contents' });
    } catch {
      // toast surfaced
    }
  };

  if (!user || user.role.slug !== 'super_admin') return <SuperAdminFallback />;

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/group-contents" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
            <ArrowLeft className="mr-1 h-4 w-4" /> กลับ
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
            แก้ไข Group Content
          </h1>
        </div>
        {query.isLoading && <div className="text-sm text-slate-400">กำลังโหลด…</div>}
        {query.error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            โหลดไม่สำเร็จ — {String(query.error)}
          </div>
        )}
        {query.data && (
          <GroupContentForm
            mode="edit"
            initial={query.data}
            onSubmit={handleSubmit}
            isSubmitting={update.isPending}
          />
        )}
      </div>
    </div>
  );
}
